// src/layouts/dashboard/index.js

import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { IconButton, Tooltip } from "@mui/material";
import Icon from "@mui/material/Icon";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DataTable from "examples/Tables/DataTable";

// Supabase
import { supabase } from "../../lib/supa";
import ServiceStatusBadge from "components/ServiceStatusBadge";

// ==========================
// Helpers
// ==========================
const fmtCurrency = (num) =>
  new Intl.NumberFormat("el-GR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(num || 0));

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("el-GR") : "");

const daysDiff = (start, end) => {
  if (!start || !end) return null;
  const a = new Date(start);
  const b = new Date(end);
  const ms = b.setHours(0, 0, 0, 0) - a.setHours(0, 0, 0, 0);
  return Math.round(ms / 86400000);
};

const unitToGreek = (unit, value) => {
  const v = Number(value || 0);
  switch (unit) {
    case "day":
    case "days":
      return v === 1 ? "ημέρα" : "ημέρες";
    case "month":
    case "months":
      return v === 1 ? "μήνας" : "μήνες";
    case "year":
    case "years":
      return v === 1 ? "έτος" : "έτη";
    default:
      return "";
  }
};

// ==========================
// Cell components (Λύση Α)
// ==========================
const PriceCell = ({ value }) => <MDTypography variant="button">{fmtCurrency(value)}</MDTypography>;
PriceCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const DateCell = ({ value }) => (
  <MDTypography variant="button">{value ? fmtDate(value) : "—"}</MDTypography>
);
DateCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date), PropTypes.number]),
};

// ==========================
// Page
// ==========================
export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // KPIs
  const [yearRevenue, setYearRevenue] = useState(0);
  const [monthRevenue, setMonthRevenue] = useState(0);
  const [servicesCount, setServicesCount] = useState(0);
  const [clientsCount, setClientsCount] = useState(0);

  // Table
  const [rows, setRows] = useState([]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const limitDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);
  // πάνω από το component (ή μέσα, πριν το return)
  const greekMonths = [
    "Ιανουάριου",
    "Φεβρουαρίου",
    "Μαρτίου",
    "Απριλίου",
    "Μαΐου",
    "Ιουνίου",
    "Ιουλίου",
    "Αυγούστου",
    "Σεπτεμβρίου",
    "Οκτωβρίου",
    "Νοεμβρίου",
    "Δεκεμβρίου",
  ];

  const monthLabel = greekMonths[today.getMonth()];

  useEffect(() => {
    async function load() {
      setLoading(true);

      // 1) KPIs από v_kpis (single row)
      const { data: kpiRow, error: kErr } = await supabase.from("v_kpis").select("*").single();

      if (kErr) console.error("v_kpis error:", kErr);

      if (kpiRow) {
        setYearRevenue(Number(kpiRow.year_revenue || 0));
        setMonthRevenue(Number(kpiRow.month_revenue || 0));
        setServicesCount(Number(kpiRow.services_count || 0));
        setClientsCount(Number(kpiRow.clients_count || 0));
      } else {
        setYearRevenue(0);
        setMonthRevenue(0);
        setServicesCount(0);
        setClientsCount(0);
      }

      // 2) Λίστα από v_services_list
      const { data: svcData, error: sErr } = await supabase
        .from("v_services_list")
        .select(
          [
            "id",
            "account_id",
            "client_id",
            "client_fullname",
            "service_type_id",
            "service_type",
            "title",
            "status",
            "start_date",
            "end_date",
            "duration_value",
            "duration_unit",
            "price",
            "created_at",
            "days_left",
          ].join(",")
        );

      if (sErr) console.error("v_services_list error:", sErr);

      const list = Array.isArray(svcData) ? svcData : [];

      // Πίνακας: έληξαν ή λήγουν στις επόμενες 30 ημέρες
      const soon = list
        .filter((s) => s.end_date && new Date(s.end_date) <= limitDate)
        .map((s) => {
          const status = s.status || (new Date(s.end_date) < today ? "Έληξε" : "Λήγει σύντομα");

          let durationText = "—";
          if (s.duration_value && s.duration_unit) {
            const u = unitToGreek(s.duration_unit, s.duration_value);
            durationText = `${s.duration_value} ${u}`.trim();
          } else {
            const dd = daysDiff(s.start_date, s.end_date);
            durationText = dd != null ? `${dd} ημέρες` : "—";
          }

          return {
            service: s.service_type || "—",
            title: s.title || "",
            client: s.client_fullname || "—",
            price: s.price ?? 0,
            // changed: use ServiceStatusBadge with numeric code and days_left
            status: (
              <ServiceStatusBadge
                statusCode={s.status}
                daysLeft={typeof s.days_left === "number" ? s.days_left : undefined}
                endDate={s.end_date || undefined}
              />
            ),
            duration: durationText,
            start_date: s.start_date || null,
            end_date: s.end_date || null,
            // NEW: services column — εμφανίζει κουμπί που πηγαίνει στο /crm/services?client=ID
            services: s.client_id ? (
              <Tooltip title="Προβολή υπηρεσιών" arrow>
                <IconButton
                  size="small"
                  onClick={() => navigate(`/services?client=${encodeURIComponent(s.client_id)}`)}
                  aria-label="Προβολή υπηρεσιών"
                >
                  <Icon color="info" fontSize="small">
                    work
                  </Icon>
                </IconButton>
              </Tooltip>
            ) : null,
          };
        })
        .sort((a, b) => new Date(a.end_date || 0) - new Date(b.end_date || 0));

      setRows(soon);
      setLoading(false);
    }

    load();
  }, [limitDate, today]);

  const columns = [
    { Header: "Ενεργειες", accessor: "services", width: "6%", align: "center" },
    { Header: "ΥΠΗΡΕΣΙΑ", accessor: "service", align: "left" },
    { Header: "ΠΕΡΙΓΡΑΦΗ", accessor: "title", align: "left" },
    { Header: "ΠΕΛΑΤΗΣ", accessor: "client", align: "left" },
    { Header: "ΤΙΜΗ", accessor: "price", align: "left", Cell: PriceCell },
    { Header: "ΚΑΤΑΣΤΑΣΗ", accessor: "status", align: "left" },
    { Header: "ΔΙΑΡΚΕΙΑ", accessor: "duration", align: "left" },
    { Header: "ΕΝΑΡΞΗ", accessor: "start_date", align: "left", Cell: DateCell },
    { Header: "ΛΗΞΗ", accessor: "end_date", align: "left", Cell: DateCell },
  ];

  // Στη λειτουργία που φτιάχνει τα rows, για κάθε πελάτη πρόσθεσε cell services
  // (π.χ. mapClients -> row object)
  const mapClientRow = (c) => ({
    service: c.service_type || "—",
    title: c.title || "",
    client: c.client_fullname || "—",
    price: c.price ?? 0,
    status: (
      <ServiceStatusBadge
        statusCode={c.status}
        daysLeft={typeof c.days_left === "number" ? c.days_left : undefined}
        endDate={c.end_date || undefined}
      />
    ),
    duration: "—",
    start_date: c.start_date || null,
    end_date: c.end_date || null,
    services: c.id ? (
      <Tooltip title="Προβολή υπηρεσιών" arrow>
        <IconButton
          size="small"
          onClick={() => {
            const url = `/crm/services?client=${encodeURIComponent(c.id)}`;
            navigate(url);
          }}
          aria-label="Προβολή υπηρεσιών"
        >
          <Icon color="info" fontSize="small">
            work
          </Icon>
        </IconButton>
      </Tooltip>
    ) : null,
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* KPI Cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="euro"
                title="Ετήσια Έσοδα Υπηρεσιών"
                count={fmtCurrency(yearRevenue)}
              />
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="wallet"
                title={`Σύνολο Εισόδων ${monthLabel}`}
                count={fmtCurrency(monthRevenue)}
              />
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="manage_accounts"
                title="Σύνολο Υπηρεσιών"
                count={servicesCount}
              />
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="person"
                title="Σύνολο Πελατών"
                count={clientsCount}
              />
            </MDBox>
          </Grid>
        </Grid>

        {/* Πίνακας: έληξαν ή λήγουν σε 30 μέρες */}
        <MDBox mt={3}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6">Υπηρεσίες που έληξαν ή λήγουν σε 30 ημέρες</MDTypography>
            </MDBox>
            <DataTable
              table={{ columns, rows }}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              canSearch={false}
              noEndBorder
              loading={loading}
            />
          </Card>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}
